import React, {useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useKeycloak} from '@react-keycloak/web';

import 'shared/styles/table.style.less';
import {FcCheckmark} from 'react-icons/all';
import {Button, Col, Divider, Form, Input, message, Radio, Row} from 'antd';
import AppTableContainer from '@crema/core/AppTableContainer';
import OrganizationSelect from 'pages/components/OrganizationSelect';
import {OrganizationContext} from './OrganizationContext';

import {findOrganizations} from 'services/OrganizationService';
import {handleError} from 'utils/MessageUtils';
import {X_TOTAL_COUNT} from 'shared/constants/AppConst';
import {EQUAL, IGNORE_CASE_LIKE} from 'shared/constants/QueryModes';

const OrganizationTable = () => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const navigate = useNavigate();
  const organizationContext = useContext(OrganizationContext);
  const [form] = Form.useForm();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const {reloading, setReloading} = organizationContext;

  const [organizations, setOrganizations] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  });
  const [loading, setLoading] = useState(false);
  const [searchValues, setSearchValues] = useState();

  const loadData = (params) => {
    setLoading(true);

    const filters = params.filters
      ? Object.keys(params.filters).map((key) => ({
          fields: key,
          value: params.filters[key],
        }))
      : [];

    if (searchValues?.name) {
      filters.push({
        fields: 'name',
        mode: IGNORE_CASE_LIKE,
        value: searchValues.name,
      });
    }

    if (searchValues?.active) {
      filters.push({
        fields: 'active',
        mode: EQUAL,
        value: searchValues.active,
      });
    }

    if (searchValues?.partOf) {
      filters.push({
        fields: 'partOf.id',
        mode: EQUAL,
        value: searchValues.partOf,
      });
    }

    findOrganizations(headers, {
      pagination: params.pagination,
      filters,
      sorters: params.sorters,
    })
      .then((res) => {
        setOrganizations(res.data);
        setPagination({
          ...params.pagination,
          total: res.headers[X_TOTAL_COUNT]
            ? parseInt(res.headers[X_TOTAL_COUNT])
            : 0,
        });
      })
      .catch((error) => handleError(message, error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData({pagination});
  }, []);

  useEffect(() => {
    if (reloading) {
      loadData({pagination});
      setReloading(false);
    }
  }, [reloading]);

  useEffect(() => {
    if (searchValues) {
      loadData({
        pagination: {...pagination, current: 1, pageSize: 20},
      });
    }

    form.resetFields();
  }, [searchValues]);

  const onView = (id) => {
    navigate(`/health/organizations/${id}`);
  };

  const onChange = (pagination, filters, sorter) => {
    loadData({pagination, filters, sorters: [sorter]});
  };

  const onSearch = (values) => {
    setSearchValues(values);
  };

  const onClearSearch = () => {
    setSearchValues({});
  };

  const columns = [
    {
      title: intl.messages['organization.name'],
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name, record) => (
        <Button type='link' onClick={() => onView(record.id)}>
          {name}
        </Button>
      ),
    },
    {
      title: intl.messages['common.enabled'],
      dataIndex: 'active',
      key: 'active',
      sorter: true,
      render: (active) => <span>{active ? <FcCheckmark /> : <></>}</span>,
    },
    {
      title: intl.messages['common.phoneNumber'],
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: intl.messages['common.email'],
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: intl.messages['organization.website'],
      dataIndex: 'website',
      key: 'website',
    },
    {
      title: intl.messages['common.modifiedDate'],
      dataIndex: 'modifiedDate',
      key: 'modifiedDate',
    },
  ];
  return (
    <>
      <div style={{padding: 10}}>
        <Form
          layout='vertical'
          form={form}
          initialValues={searchValues}
          onFinish={onSearch}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name='name' label={intl.messages['organization.name']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name='partOf'
                label={intl.messages['organization.partOf']}>
                <OrganizationSelect />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name='active' label={intl.messages['common.enabled']}>
                <Radio.Group>
                  <Radio value='true'>{intl.messages['common.yes']}</Radio>
                  <Radio value='false'>{intl.messages['common.no']}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={10} />
            <Col span={2}>
              <Button onClick={onClearSearch} style={{width: '100%'}}>
                {intl.messages['common.reset']}
              </Button>
            </Col>
            <Col span={2}>
              <Button type='primary' htmlType='submit' style={{width: '100%'}}>
                {intl.messages['common.search']}
              </Button>
            </Col>
            <Col span={10} />
          </Row>
        </Form>
      </div>

      <Divider />

      <AppTableContainer
        className='customer-table'
        hoverColor
        data={organizations}
        columns={columns}
        rowKey={(record) => record.id}
        loading={loading}
        pagination={pagination}
        onChange={onChange}
        scroll={{x: 'auto'}}
      />
    </>
  );
};

export default OrganizationTable;
